import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import { mtsCanView, mtsDarkMode, setMtsDarkMode } from "../mts-reporting-summary/mts-theme";

const REQUEST_TIMEOUT_MS = 30000;

// One row per reason, which is what the report IS.
interface Row {
  key: string; // the rejection code, as stored
  label: string; // what it means, resolved by the API from the shared vocabulary
  rejectedBets: number;
  rejectedTurnover: number;
  acceptedBets: number;
}

@Component({
  selector: "app-mts-rejections",
  templateUrl: "./mts-rejections.component.html",
  styleUrls: ["./mts-rejections.component.scss"],
})
export class MtsRejectionsComponent implements OnInit {
  canView = mtsCanView();
  checked = false;
  dark = mtsDarkMode();

  loading = false;
  errorMessage = "";
  rows: Row[] = [];

  // Every bet ATTEMPTED in the range: the denominator the "% of attempted"
  // column is measured against. Taken from the API's own totals rather than
  // summed from the rows, so a row the page could not render cannot silently
  // shrink it.
  attempted = 0;
  totalRejected = 0;

  groupProvider = "SportsBook";
  // SEVEN DAYS, because that is the longest range the API will answer.
  //
  // This opened on THIRTY and offered a thirty-day preset, so the page loaded
  // straight into "the range is longer than 7 days" before anyone had touched
  // a control - and the one preset most likely to be clicked was another
  // error. The sibling pages carry the same cap and the same note; this one
  // was written without it.
  selected: any = {
    startDate: moment().subtract(6, "days").startOf("day"),
    endDate: moment().endOf("day"),
  };
  ranges: any = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [
      moment().subtract(1, "days").startOf("day"),
      moment().subtract(1, "days").endOf("day"),
    ],
    "Last 7 days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
  };

  constructor(private appService: AppService, private router: Router) {}

  ngOnInit() {
    // Re-read, like the sibling pages: the snapshot taken at construction can
    // predate the session being written on a cold load of this URL.
    this.canView = mtsCanView();
    this.checked = true;
    if (this.canView) {
      this.load();
    }
  }

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
  }

  back() {
    this.router.navigate(["/mts/reporting-summary"]);
  }

  onDateChangeHandler(_: any) {}
  apply() {
    this.load();
  }

  // The same refusals cut three ways: by reason, by sport, and the events
  // that produced most of them. One range and one filter bar for all three, so
  // they cannot describe different books.
  sportRows: Row[] = [];
  eventRows: Row[] = [];

  // Ten, which is what makes it a list to scan rather than a second report.
  readonly topEvents = 10;

  private loadSeq = 0;

  load() {
    this.loadBreakdown("sport", (rows) => (this.sportRows = rows));
    // RANKED BY REFUSALS, not by turnover. The events with the most money are
    // not the events with the most refusals, and this report is about the
    // latter - see the API's sortBy.
    this.loadBreakdown(
      "event",
      (rows) => (this.eventRows = rows),
      { sortBy: "rejected", displayCount: this.topEvents }
    );

    const seq = ++this.loadSeq;
    this.loading = true;
    this.errorMessage = "";

    const payload: any = {
      startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
      endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
      groupProvider: this.groupProvider,
      groupBy: "rejectreason",
    };

    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.loadSeq) {
            return; // a newer load started; this reply is for the old filter
          }
          this.loading = false;
          if (!res || res.status !== "RS_OK") {
            this.errorMessage =
              (res && res.errorDescription) || "This report could not be loaded.";
            this.rows = [];
            return;
          }
          const all: Row[] = res.rows || [];

          // The grouping returns one row per reason PLUS one unlabelled row
          // holding everything that was not rejected. That row is not a
          // reason and must not be listed as one - but it is most of the
          // denominator, so it is taken out and kept rather than dropped.
          this.rows = all
            .filter((r) => !!r.key)
            .sort((a, b) => (b.rejectedBets || 0) - (a.rejectedBets || 0));

          const totals = res.totals || {};
          this.totalRejected = totals.rejectedBets || 0;
          this.attempted = (totals.acceptedBets || 0) + this.totalRejected;
        },
        () => {
          if (seq !== this.loadSeq) {
            return;
          }
          this.loading = false;
          this.errorMessage = "This report could not be loaded.";
          this.rows = [];
        }
      );
  }

  // Share of everything ATTEMPTED, not of the rejections alone - so the column
  // answers "how often is this happening", not "of the ones we refused, which".
  // One extra breakdown over the same range and filters.
  //
  // Its own ticket, like the main load: changing a filter fires three requests
  // and they do not come back in order, so a slow reply for the old filter
  // must not land on top of a fast one for the new.
  private extraSeq: { [k: string]: number } = { sport: 0, event: 0 };

  private loadBreakdown(groupBy: string, assign: (rows: Row[]) => void, extra: any = {}) {
    const seq = ++this.extraSeq[groupBy];
    const payload: any = {
      startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
      endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
      groupProvider: this.groupProvider,
      groupBy,
      ...extra,
    };
    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.extraSeq[groupBy]) {
            return;
          }
          // Rows with NO refusals are dropped: this report is about what was
          // refused, and a list of sports that refused nothing is noise.
          assign(
            res && res.status === "RS_OK"
              ? (res.rows || []).filter((r: Row) => (r.rejectedBets || 0) > 0)
              : []
          );
        },
        // A breakdown that fails leaves its own table empty and says nothing.
        // The reason table is the report; these two are beside it.
        () => {
          if (seq === this.extraSeq[groupBy]) {
            assign([]);
          }
        }
      );
  }

  share(r: Row): number {
    if (!this.attempted) {
      return 0;
    }
    return (100 * (r.rejectedBets || 0)) / this.attempted;
  }

  money(v: number): string {
    const n = Number(v || 0);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  downloadCsv() {
    const head = ["Rejection reason", "Rejection code", "Rejected bets", "% of attempted", "Rejected turnover"];
    const lines = [head.join(",")];
    for (const r of this.rows) {
      lines.push(
        [
          `"${(r.label || "").replace(/"/g, '""')}"`,
          r.key,
          r.rejectedBets || 0,
          this.share(r).toFixed(2),
          (r.rejectedTurnover || 0).toFixed(2),
        ].join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "rejected-bets.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
